<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { Menu, X } from '@lucide/vue';
import { Button } from '@/components/ui/button';
import {
  APP_CTA_TO,
  APP_CTA_LABEL,
  TELEGRAM_CTA_LABEL,
  TELEGRAM_BOT_URL,
  SITE_NAV_LINKS,
} from '@app/presentation/components/site/constants';
import BrandLogo from '@app/presentation/components/shared/BrandLogo.vue';
import PaperStrip from '@app/presentation/components/paper/PaperStrip.vue';

// Sticky scroll-aware nav. `scrolled` is a plain client-only ref that starts
// false, so the SSR / first-client render ships the not-scrolled state and Vue
// hydrates in place. The scroll listener is attached only in onMounted (no
// window access at module/setup scope → SSG-safe) and torn down in onUnmounted.
const scrolled = ref(false);

let onScroll: (() => void) | null = null;

onMounted(() => {
  onScroll = () => {
    scrolled.value = window.scrollY > 12;
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  // Sync once in case the page loads already scrolled (e.g. anchor / back-nav).
  onScroll();
});

onUnmounted(() => {
  document.body.style.overflow = '';
  if (onScroll) {
    window.removeEventListener('scroll', onScroll);
    onScroll = null;
  }
});

// Mobile menu toggle. Plain client-only ref; the pre-rendered HTML ships the
// panel collapsed (menuOpen === false), matching the client's first render.
const menuOpen = ref(false);

// Lock body scroll while the mobile dropdown is open so the page behind it
// doesn't scroll. Watchers don't run during SSR pre-render, so `document` is
// never touched at pre-render time; onUnmounted is client-only.
watch(menuOpen, (open) => {
  document.body.style.overflow = open ? 'hidden' : '';
});

// Active-route highlight. Resolved from vue-router (works during SSG pre-render
// too), so each pre-rendered page bakes in its own active link and the client's
// first render matches → hydrates in place. A spread detail `/rasklady/:slug`
// keeps "Расклады" lit via the startsWith match.
const route = useRoute();
const isActive = (to: string) =>
  to === '/' ? route.path === '/' : route.path.startsWith(to);
</script>

<template>
  <!-- Sticky torn paper strip (PaperStrip: card stock, torn bottom edge, lit
       fibre). Solid stock, no blur; `scrolled` only adds the cast shade. -->
  <nav class="sticky top-0 z-40">
    <PaperStrip edge="bottom" wide :shade="scrolled" />
    <div
      class="site-container flex items-center justify-between h-[60px] md:h-[68px]"
    >
      <!-- Brand -->
      <BrandLogo />

      <!-- Center links + active highlight (a cream paper tag) -->
      <div class="hidden md:flex items-center gap-2">
        <RouterLink
          v-for="link in SITE_NAV_LINKS"
          :key="link.to"
          :to="link.to"
          :class="[
            'rounded-[var(--paper-radius-xs)] px-3.5 py-2 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50',
            isActive(link.to)
              ? 'paper-tag shadow-[0_1px_0_rgb(4_3_18/0.5),0_4px_8px_-4px_var(--paper-shade)]'
              : 'text-muted-foreground hover:bg-foreground/8 hover:text-foreground',
          ]"
        >
          {{ link.label }}
        </RouterLink>
      </div>

      <!-- Right: primary app CTA + secondary Telegram link + mobile hamburger -->
      <div class="flex items-center gap-2 md:gap-4">
        <a
          :href="TELEGRAM_BOT_URL"
          target="_blank"
          rel="noopener"
          class="hidden text-sm text-muted-foreground transition-colors hover:text-foreground lg:inline"
        >
          {{ TELEGRAM_CTA_LABEL }}
        </a>

        <Button
          as-child
          class="hidden md:inline-flex"
        >
          <RouterLink :to="APP_CTA_TO">{{ APP_CTA_LABEL }}</RouterLink>
        </Button>

        <Button
          variant="ghost"
          size="icon"
          class="md:hidden"
          :aria-label="menuOpen ? 'Закрыть меню' : 'Открыть меню'"
          :aria-expanded="menuOpen"
          @click="menuOpen = !menuOpen"
        >
          <X v-if="menuOpen" class="h-5 w-5" />
          <Menu v-else class="h-5 w-5" />
        </Button>
      </div>
    </div>

    <!-- Mobile dropdown panel: a raised sheet with a torn top edge, laid over
         the strip's tear. Its long cast is a box-shadow on the (unclipped)
         panel box; only the PaperStrip backing is clipped. -->
    <div
      v-if="menuOpen"
      class="relative isolate -mt-1 shadow-[0_14px_30px_-10px_var(--paper-shade)] md:hidden"
    >
      <PaperStrip edge="top" stock="popover" />
      <nav class="site-container flex flex-col gap-1 pt-4 pb-3">
        <RouterLink
          v-for="link in SITE_NAV_LINKS"
          :key="link.to"
          :to="link.to"
          :class="[
            'block rounded-[var(--paper-radius-xs)] px-3 py-2.5 text-sm font-medium transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-paper-stock/50',
            isActive(link.to)
              ? 'paper-tag'
              : 'text-muted-foreground hover:bg-foreground/8 hover:text-foreground',
          ]"
          @click="menuOpen = false"
        >
          {{ link.label }}
        </RouterLink>

        <div class="my-2 border-t border-dashed border-foreground/20" />

        <Button
          as-child
          class="w-full"
        >
          <RouterLink :to="APP_CTA_TO" @click="menuOpen = false">
            {{ APP_CTA_LABEL }}
          </RouterLink>
        </Button>
        <Button as-child variant="outline" class="mt-2 w-full">
          <a
            :href="TELEGRAM_BOT_URL"
            target="_blank"
            rel="noopener"
            @click="menuOpen = false"
          >
            {{ TELEGRAM_CTA_LABEL }}
          </a>
        </Button>
      </nav>
    </div>
  </nav>
</template>
